import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatCardModule} from '@angular/material/card';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {MatDividerModule} from '@angular/material/divider';
import {MatListModule} from '@angular/material/list';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatTabsModule} from '@angular/material/tabs';
import {RestFactoryService} from '../../../core/services/rest-factory.service';
import {debounceTime, distinctUntilChanged, finalize, take} from 'rxjs';
import {NgxQrcodeStylingComponent, Options} from 'ngx-qrcode-styling';

export type SkinConfig = {
    name: string;
    desktopUrl: string;
    mobileKey: string | null;
    mainLogo: string | null;
    sideLogo: string | null;
    headerFooterColor: string;
    themeColor: string;
};

export const skins: Record<string, SkinConfig> = {
    global: {
        name: "Kixa",
        desktopUrl: "https://global.mykixa.com/sign-up",
        mobileKey: null,
        mainLogo: "https://global.mykixa.com/images/kixa-blackold.png",
        sideLogo: null,
        headerFooterColor: "rgb(246 246 246)",
        themeColor: "rgb(184 156 76)",
    },
    naturalsSpa: {
        name: "Naturals SPA",
        desktopUrl: "https://naturals.mykixa.com/sign-up",
        mobileKey: "naturals",
        mainLogo: "https://naturals.mykixa.com/images/kixa-offered-b.png",
        sideLogo: "https://media.myaltituderewards.com/logos/naturals_header_logo.png",
        headerFooterColor: "rgb(152 108 173)",
        themeColor: "rgb(126 71 152)",
    },
    airIndiaExpress: {
        name: "Airindiaexpress",
        desktopUrl: "https://airindiaexpress.mykixa.com/sign-up",
        mobileKey: "airindiaexpress",
        mainLogo: "https://airindiaexpress.mykixa.com/images/kixa-offered-b.png",
        sideLogo: "https://media.myaltituderewards.com/logos/air-india-express.png",
        headerFooterColor: "rgb(255 255 255)",
        themeColor: "rgb(126 71 152)",
    },
    jazeera: {
        name: "Jazeera",
        desktopUrl: "https://jazeera.mykixa.com/sign-up",
        mobileKey: "jazeera",
        mainLogo: "https://jazeera.mykixa.com/images/kixa-offered-b.png",
        sideLogo: "https://media.myaltituderewards.com/logos/jazeera.png",
        headerFooterColor: "rgb(248 252 253)",
        themeColor: "rgb(0 76 114)",
    },
    aeroitalia: {
        name: "Aeroitalia",
        desktopUrl: "https://aeroitalia.mykixa.com/sign-up",
        mobileKey: "aeroitalia",
        mainLogo: "https://aeroitalia.mykixa.com/images/kixa-offered-b.png",
        sideLogo: "https://media.myaltituderewards.com/logos/aeroitalia.png",
        headerFooterColor: "rgb(255 255 255)",
        themeColor: "rgb(50 128 64)",
    },
    jejuair: {
        name: "Jejuair",
        desktopUrl: "https://jejuair.mykixa.com/sign-up",
        mobileKey: "jejuair",
        mainLogo: "https://jejuair.mykixa.com/images/kixa-offered-b.png",
        sideLogo: "https://media.myaltituderewards.com/logos/jejuair.png",
        headerFooterColor: "rgb(248 248 248)",
        themeColor: "rgb(255 80 0)",
    },
    aloha: {
        name: "Aloha",
        desktopUrl: "https://aloha.mykixa.com/sign-up",
        mobileKey: "aloha",
        mainLogo: "https://media.myaltituderewards.com/logos/kixa-aloha-light-theme-logo.png",
        sideLogo: null,
        headerFooterColor: "rgb(249 253 252)",
        themeColor: "rgb(59 191 191)",
    },
};

interface BusinessSearchResult {
    id_destination: string;
    ds_name: string;
    ds_destinationtype: string;
    ds_label: string;
    im_mediacard: string;
    ggparent?: { ds_name: string };
}

interface BusinessDetail {
    id_destination: string;
    ds_name: string;
    tx_description: string;
    im_media: string;
}

interface GeneratedLinks {
    mobileUrl: string;
    desktopUrl: string;
    label: string;
}

@Component({
    selector: 'app-qr-link-generator',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatProgressBarModule,
        MatCardModule,
        MatSnackBarModule,
        MatDividerModule,
        MatListModule,
        MatTooltipModule,
        MatDialogModule,
        MatTabsModule,
        NgxQrcodeStylingComponent
    ],
    templateUrl: './qr-link-generator.component.html',
    styleUrls: ['./qr-link-generator.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QrLinkGeneratorComponent implements OnInit {
    @ViewChild('mobileQrCode', { static: false }) mobileQrCode!: NgxQrcodeStylingComponent;
    @ViewChild('desktopQrCode', { static: false }) desktopQrCode!: NgxQrcodeStylingComponent;

    @ViewChild('bigMobileQrCode', { static: false }) bigMobileQrCode!: NgxQrcodeStylingComponent;
    @ViewChild('bigDesktopQrCode', { static: false }) bigDesktopQrCode!: NgxQrcodeStylingComponent;

    skinList = Object.values(skins);
    selectedSkin: SkinConfig | null = null;

    searchForm: FormGroup;
    isLoading = false;
    isGenerating = false;

    searchResults: BusinessSearchResult[] = [];
    selectedBusiness: BusinessDetail | null = null;
    generatedLinks: GeneratedLinks | null = null;

    baseQrConfig: Options = {
        width: 300,
        height: 300,
        type: 'canvas',
        margin: 10,
        data: '',
        image: '',
        qrOptions: {
            typeNumber: 0,
            mode: 'Byte',
            errorCorrectionLevel: 'H'
        },
        imageOptions: {
            hideBackgroundDots: true,
            imageSize: 0.45,
            margin: 5,
            crossOrigin: 'anonymous',
        },
        dotsOptions: { color: '#000000', type: 'square' },
        backgroundOptions: { color: '#ffffff' },
        cornersSquareOptions: { color: '#000000', type: 'square' },
        cornersDotOptions: { color: '#000000', type: 'square' }
    };

    mobileQrConfig: Options = { ...this.baseQrConfig };
    desktopQrConfig: Options = { ...this.baseQrConfig };

    bigMobileQrConfig: Options = { ...this.baseQrConfig, width: 1024, height: 1024 };
    bigDesktopQrConfig: Options = { ...this.baseQrConfig, width: 1024, height: 1024 };

    private readonly API_SEARCH = '/front/search/places/name/listall';
    private readonly API_GET_DETAIL = '/destination/get';
    private readonly API_GET_ADMIN_QR = '/admin/marketing/getdownloadqr';

    constructor(
        private _fb: FormBuilder,
        private _restFactoryService: RestFactoryService,
        private _cd: ChangeDetectorRef,
        private _snackBar: MatSnackBar,
        private _dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.searchForm = this._fb.group({
            ds_search: ['', [Validators.required, Validators.minLength(3)]]
        });

        this.searchForm.get('ds_search')?.valueChanges
            .pipe(debounceTime(400), distinctUntilChanged())
            .subscribe(() => {
                if (this.searchForm.valid) {
                    this.searchBusiness();
                } else if (this.searchForm.value.ds_search.length >= 1) {
                    this.clearResultsState();
                }
            });
    }

    selectSkin(skin: SkinConfig): void {
        this.selectedSkin = skin;
        this.clearSearch();
        this._cd.markForCheck();
    }

    backToSkins(): void {
        this.selectedSkin = null;
        this.clearSearch();
    }

    searchBusiness(): void {
        const searchTerm = this.searchForm.get('ds_search')?.value;
        if (this.searchForm.invalid || !searchTerm) return;

        this.isLoading = true;
        this.clearResultsState();

        const payload = new FormData();
        payload.append('ds_search', searchTerm);

        this._restFactoryService.httpPostService(this.API_SEARCH, payload)
            .pipe(
                take(1),
                finalize(() => {
                    this.isLoading = false;
                    this._cd.markForCheck();
                })
            )
            .subscribe({
                next: (response: any) => {
                    if (response && response.ar_results) {
                        this.searchResults = response.ar_results;
                        if (this.searchResults.length === 0) {
                            this._snackBar.open('No businesses found.', 'Close', { duration: 3000 });
                        }
                    }
                },
                error: (error) => {
                    this._snackBar.open('Error during search.', 'Close', { duration: 3000 });
                }
            });
    }

    selectBusiness(business: BusinessSearchResult): void {
        this.getBusinessDetails(business.id_destination);
        this.searchResults = [];
    }

    getBusinessDetails(id: string): void {
        this.isLoading = true;
        this.selectedBusiness = null;
        this.generatedLinks = null;

        const payload = new FormData();
        payload.append('id_destination', id);
        payload.append('bl_preview', "1");

        this._restFactoryService.httpPostService(this.API_GET_DETAIL, payload)
            .pipe(
                take(1),
                finalize(() => {
                    this.isLoading = false;
                    this._cd.markForCheck();
                })
            )
            .subscribe({
                next: (response: any) => {
                    if (response && response.destination) {
                        const dest = response.destination;
                        this.selectedBusiness = {
                            id_destination: dest.id_destination,
                            ds_name: dest.ds_name,
                            tx_description: dest.tx_description,
                            im_media: dest.im_media
                        };
                    } else {
                        this._snackBar.open('Could not load business details.', 'Close', { duration: 3000 });
                    }
                },
                error: (error) => {
                    this._snackBar.open('Error fetching business details.', 'Close', { duration: 3000 });
                }
            });
    }

    generateAdminLink(): void {
        if (!this.selectedBusiness || !this.selectedSkin) return;

        this.isGenerating = true;
        this.generatedLinks = null;

        const payload = new FormData();
        payload.append('id_destination', this.selectedBusiness.id_destination);

        this._restFactoryService.httpPostService(this.API_GET_ADMIN_QR, payload)
            .pipe(
                take(1),
                finalize(() => {
                    this.isGenerating = false;
                    this._cd.markForCheck();
                })
            )
            .subscribe({
                next: (response: any) => {
                    if (response && response.ds_url && response.ds_label) {
                        const originalUrl = response.ds_url as string;
                        const label = response.ds_label;

                        let mobileUrl = originalUrl;
                        if (this.selectedSkin!.mobileKey) {
                            mobileUrl = originalUrl.replace('global', this.selectedSkin!.mobileKey);
                        }

                        let desktopUrl = this.selectedSkin!.desktopUrl;
                        try {
                            const urlObj = new URL(originalUrl);
                            const params = urlObj.search;
                            desktopUrl = `${desktopUrl}${params}`;
                        } catch (e) {
                            console.error("Error parsing URL params", e);
                        }

                        this.generatedLinks = {
                            mobileUrl,
                            desktopUrl,
                            label
                        };

                        const themeColor = this.selectedSkin?.themeColor || '#000000';
                        const labelImage = this.createLabelImage(label, themeColor);

                        this.mobileQrConfig = { ...this.baseQrConfig, data: mobileUrl, image: labelImage };
                        this.desktopQrConfig = { ...this.baseQrConfig, data: desktopUrl, image: labelImage };

                        this.bigMobileQrConfig = { ...this.baseQrConfig, width: 1024, height: 1024, data: mobileUrl, image: labelImage };
                        this.bigDesktopQrConfig = { ...this.baseQrConfig, width: 1024, height: 1024, data: desktopUrl, image: labelImage };

                        this._snackBar.open('Links Generated!', 'Dismiss', { duration: 3000 });
                        this._cd.markForCheck();
                    } else {
                        this._snackBar.open('Invalid response from server.', 'Close', { duration: 3000 });
                    }
                },
                error: (error) => {
                    this._snackBar.open('Error generating link.', 'Close', { duration: 3000 });
                }
            });
    }

    clearSearch(): void {
        this.searchForm.reset();
        this.clearResultsState();
        this._cd.markForCheck();
    }

    downloadQr(type: 'mobile' | 'desktop'): void {
        const name = (this.selectedBusiness?.ds_name || 'qr') + `-${type}.png`;
        if (type === 'mobile' && this.bigMobileQrCode) {
            this.bigMobileQrCode.download(name);
        } else if (type === 'desktop' && this.bigDesktopQrCode) {
            this.bigDesktopQrCode.download(name);
        }
    }

    openZoomedQr(templateRef: TemplateRef<any>): void {
        this._dialog.open(templateRef, {
            maxWidth: '98vw',
            maxHeight: '98vh',
            width: 'auto',
            height: 'auto',
            panelClass: 'bg-transparent',
            backdropClass: 'backdrop-blur-sm',
            autoFocus: false
        });
    }

    copyUrl(url: string | undefined): void {
        if (url) {
            navigator.clipboard.writeText(url).then(() => {
                this._snackBar.open('URL copied!', 'Close', { duration: 2000 });
            });
        }
    }

    private clearResultsState(): void {
        this.searchResults = [];
        this.selectedBusiness = null;
        this.generatedLinks = null;
        this.mobileQrConfig = { ...this.baseQrConfig, data: '', image: '' };
        this.desktopQrConfig = { ...this.baseQrConfig, data: '', image: '' };
    }

    private createLabelImage(text: string, color: string): string {
        const size = 1024;
        const padding = 20;
        const borderThickness = 40;
        const center = size / 2;
        const radius = (size / 2) - padding;

        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext('2d');
        if (!ctx) return '';

        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(center, center, radius, 0, 2 * Math.PI);
        ctx.fill();

        ctx.lineWidth = borderThickness;
        ctx.strokeStyle = color;
        ctx.stroke();

        const safeWidth = radius * 1.5;
        const safeHeight = radius * 1.5;

        ctx.fillStyle = '#222222';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        let fontSize = 200;
        let lines: string[] = [];
        let lineHeight = 0;
        const minFontSize = 40;

        while (fontSize >= minFontSize) {
            ctx.font = `bold ${fontSize}px "Roboto", "Helvetica Neue", "Arial", sans-serif`;
            lineHeight = fontSize * 1.15;
            lines = this.getWrappedLines(ctx, text, safeWidth);
            const totalHeight = lines.length * lineHeight;
            if (totalHeight <= safeHeight) break;
            fontSize -= 10;
        }

        const totalBlockHeight = lines.length * lineHeight;
        const startY = center - (totalBlockHeight / 2) + (lineHeight / 2) - (fontSize * 0.1);

        lines.forEach((line, index) => {
            ctx.fillText(line, center, startY + (index * lineHeight));
        });

        return canvas.toDataURL();
    }

    private getWrappedLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
        const words = text.split(' ');
        const lines: string[] = [];
        let currentLine = words[0];

        const processToken = (token: string): string[] => {
            if (ctx.measureText(token).width <= maxWidth) return [token];
            const splitByChar = token.split(/([\/,-])/);
            let subLines: string[] = [];
            let subLine = "";

            for (let part of splitByChar) {
                if (ctx.measureText(subLine + part).width <= maxWidth) {
                    subLine += part;
                } else {
                    if (subLine) subLines.push(subLine);
                    if (ctx.measureText(part).width > maxWidth) {
                        let charLine = "";
                        for (let char of part) {
                            if (ctx.measureText(charLine + char).width <= maxWidth) {
                                charLine += char;
                            } else {
                                subLines.push(charLine);
                                charLine = char;
                            }
                        }
                        subLine = charLine;
                    } else {
                        subLine = part;
                    }
                }
            }
            if (subLine) subLines.push(subLine);
            return subLines;
        };

        if (ctx.measureText(currentLine).width > maxWidth) {
            const broken = processToken(currentLine);
            lines.push(...broken.slice(0, -1));
            currentLine = broken[broken.length - 1];
        }

        for (let i = 1; i < words.length; i++) {
            const word = words[i];
            const testLine = currentLine + " " + word;

            if (ctx.measureText(testLine).width <= maxWidth) {
                currentLine = testLine;
            } else {
                lines.push(currentLine);
                if (ctx.measureText(word).width > maxWidth) {
                    const broken = processToken(word);
                    lines.push(...broken.slice(0, -1));
                    currentLine = broken[broken.length - 1];
                } else {
                    currentLine = word;
                }
            }
        }
        lines.push(currentLine);
        return lines;
    }
}
